import React, { useEffect, useState } from "react";
import Modal from "./Modal";
import { useData } from "../context/DataContext";
import {
  formatKES,
  formatDate,
  currentMonth,
  tenantBalance,
  tenantNextDueDate,
  computeTenantStatus,
  today,
  utilityBalanceForMonth,
  utilityChargeForMonth,
} from "../utils/format";
import { MessageSquare, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import StatusBadge from "./StatusBadge";

function buildMessage(tenant, payments, utilityCharges, property) {
  const month = currentMonth();
  const status = computeTenantStatus(tenant, payments, month);
  const rent = Number(tenant.rent) || 0;
  const balance = tenantBalance(tenant, payments, month);
  const nextDueIso = tenantNextDueDate(tenant, payments);
  const dueLabel =
    nextDueIso === today()
      ? `${formatDate(nextDueIso)} (today)`
      : formatDate(nextDueIso);

  // Build utility detail if applicable.
  const parts = [];
  if (property?.waterEnabled) {
    const wb = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "water");
    if (wb > 0) parts.push(`water (${formatKES(wb)})`);
  }
  if (property?.electricityEnabled) {
    const eb = utilityBalanceForMonth(utilityCharges, payments, tenant.id, month, "electricity");
    if (eb > 0) parts.push(`electricity (${formatKES(eb)})`);
  }
  const utilityNote = parts.length > 0
    ? ` You also have outstanding ${parts.join(" and ")}.`
    : "";

  switch (status) {
    case "PAID":
      return `Hi ${tenant.name}, thank you for paying your rent of ${formatKES(rent)} for house ${tenant.houseNumber}. Your next rent is due on ${dueLabel}. We appreciate your timely payment!${utilityNote}`;
    case "PARTIAL":
      return `Hi ${tenant.name}, thank you for your partial payment on house ${tenant.houseNumber}. A rent balance of ${formatKES(balance)} remains outstanding.${utilityNote} Please complete payment at your earliest convenience. Thank you.`;
    case "OVERDUE":
      return `Hi ${tenant.name}, your rent for house ${tenant.houseNumber} is now OVERDUE. An amount of ${formatKES(balance)} is outstanding.${utilityNote} Kindly settle immediately to avoid penalties. Thank you.`;
    case "PENDING":
    default:
      return `Hi ${tenant.name}, this is a friendly reminder that your rent of ${formatKES(rent)} for house ${tenant.houseNumber} is due on ${dueLabel}.${utilityNote} Please arrange payment on time. Thank you.`;
  }
}

export default function SmsDialog({ open, onClose, tenant }) {
  const { addCommunication, payments, utilityCharges, properties } = useData();
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  // null = not sent yet, "sent" | "failed"
  const [result, setResult] = useState(null);

  const status = tenant
    ? computeTenantStatus(tenant, payments, currentMonth())
    : null;

  const property = properties.find((p) => p.id === tenant?.propertyId);

  useEffect(() => {
    if (!open || !tenant) return;
    setMessage(buildMessage(tenant, payments, utilityCharges, property));
    setResult(null);
  }, [open, tenant, payments, utilityCharges, property]);

  if (!tenant) return null;

  const handleClose = () => {
    setResult(null);
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    setResult(null);
    try {
      const c = await addCommunication({ tenantId: tenant.id, type: "sms", message: message.trim() });
      setResult(c?.status === "failed" ? "failed" : "sent");
    } catch (err) {
      console.error("SMS send failed:", err);
      setResult("failed");
    }
    setSending(false);
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Send SMS reminder"
      description={`${tenant.name} · ${tenant.phone || "no phone"}`}
    >
      {result ? (
        <div className="space-y-4">
          {result === "sent" ? (
            <div className="flex items-start gap-3 rounded-lg border border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950 p-4">
              <CheckCircle2 className="h-5 w-5 flex-none text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-green-800 dark:text-green-300">SMS sent successfully</p>
                <p className="mt-1 text-sm text-green-700 dark:text-green-400">
                  Message delivered to {tenant.phone || tenant.name}.
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950 p-4">
              <XCircle className="h-5 w-5 flex-none text-red-600 dark:text-red-400 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-red-800 dark:text-red-300">SMS failed to send</p>
                <p className="mt-1 text-sm text-red-700 dark:text-red-400">
                  Could not deliver to {tenant.phone || tenant.name}. Please check the phone number and try again.
                </p>
              </div>
            </div>
          )}
          <div className="flex justify-end">
            <button type="button" className="btn-primary" onClick={handleClose}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">Current status:</span>
            <StatusBadge status={status?.toLowerCase() || "pending"} />
          </div>
          <div>
            <label className="label-base">Message</label>
            <textarea
              className="input-base min-h-[120px] resize-y"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {message.length} characters · {Math.ceil(message.length / 160)} SMS
            </p>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MessageSquare className="h-4 w-4" />}
              {sending ? "Sending…" : "Send SMS"}
            </button>
          </div>
        </form>
      )}
    </Modal>
  );
}
